import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import * as d3 from 'd3'
import { parseDay, teamChange } from './data'
import { useElementWidth } from './useElementSize'
import { RankDelta, ScoreDelta, describeEvent, formatDay, formatScore } from './ui'

const MARGIN = { top: 16, right: 16, bottom: 26, left: 40 }
const LINE_H = 190
const GAP = 22
const BARS_H = 64
const HEIGHT = MARGIN.top + LINE_H + GAP + BARS_H + MARGIN.bottom
const tickFormat = d3.utcFormat('%b %-d')

// The team's score over time, with a strip of per-snapshot score changes
// underneath (its own scale - the two are never overlaid on one axis).
export default function TeamScoreChart({ league, team, dates, pinnedDate, onPinDate }) {
  const [ref, width] = useElementWidth()
  const [hoverIdx, setHoverIdx] = useState(null)
  const innerW = Math.max(0, width - MARGIN.left - MARGIN.right)

  const model = useMemo(() => {
    const values = dates.map(d => team.series[league.dateIndex.get(d)]).map((v, i) => (v ? { ...v, date: dates[i] } : null))
    const deltas = dates.map((d, i) => {
      if (i === 0 || !values[i] || !values[i - 1]) return { date: d, delta: 0 }
      return { date: d, delta: values[i].score - values[i - 1].score }
    })
    const x = d3.scaleUtc().domain(d3.extent(dates, parseDay)).range([0, innerW])
    const scores = values.filter(Boolean).map(v => v.score)
    const [lo, hi] = d3.extent(scores)
    const pad = Math.max(0.15, (hi - lo) * 0.15)
    const y = d3.scaleLinear().domain([lo - pad, hi + pad]).nice().range([LINE_H, 0])
    const maxDelta = Math.max(0.05, d3.max(deltas, d => Math.abs(d.delta)))
    const yd = d3.scaleLinear().domain([-maxDelta, maxDelta]).range([BARS_H, 0])
    const line = d3.line().defined(d => d).x(d => x(parseDay(d.date))).y(d => y(d.score)).curve(d3.curveStepAfter)
    const area = d3.area().defined(d => d).x(d => x(parseDay(d.date))).y0(LINE_H).y1(d => y(d.score)).curve(d3.curveStepAfter)
    const barW = Math.max(2, Math.min(8, (innerW / dates.length) * 0.6))
    return { values, deltas, x, y, yd, linePath: line(values) || '', areaPath: area(values) || '', barW, maxDelta }
  }, [league, team, dates, innerW])

  const { values, deltas, x, y, yd, linePath, areaPath, barW } = model
  const xs = useMemo(() => dates.map(d => x(parseDay(d))), [dates, x])
  const gradientId = `hv-grad-${team.acronym}`

  const handleMove = e => {
    const rect = e.currentTarget.getBoundingClientRect()
    setHoverIdx(d3.bisectCenter(xs, e.clientX - rect.left))
  }

  const hoverDate = hoverIdx != null ? dates[hoverIdx] : null
  const hoverValue = hoverIdx != null ? values[hoverIdx] : null
  const hoverChange = hoverDate ? teamChange(league, team, hoverDate) : null
  const barsTop = LINE_H + GAP

  return (
    <div className="hv-chart hv-chart--team" ref={ref} style={{ height: HEIGHT, '--team': team.accent }}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={`${team.name} score over time`}>
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={team.accent} stopOpacity="0.28" />
              <stop offset="100%" stopColor={team.accent} stopOpacity="0" />
            </linearGradient>
          </defs>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {y.ticks(4).map(t => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line className="hv-grid" x2={innerW} />
                <text className="hv-axis-label" x={-10} dy="0.32em" textAnchor="end">{t.toFixed(1)}</text>
              </g>
            ))}
            <text className="hv-axis-title" x={0} y={barsTop - 8}>Change per update</text>
            <line className="hv-zero" x2={innerW} y1={barsTop + yd(0)} y2={barsTop + yd(0)} />
            {x.ticks(Math.max(2, Math.floor(innerW / 110))).map(t => (
              <text key={+t} className="hv-axis-label" x={x(t)} y={barsTop + BARS_H + 20} textAnchor="middle">
                {tickFormat(t)}
              </text>
            ))}

            {pinnedDate && pinnedDate >= dates[0] && (
              <line className="hv-pin-line" x1={x(parseDay(pinnedDate))} x2={x(parseDay(pinnedDate))} y2={barsTop + BARS_H} />
            )}
            {hoverIdx != null && <line className="hv-hover-line" x1={xs[hoverIdx]} x2={xs[hoverIdx]} y2={barsTop + BARS_H} />}

            <clipPath id={`${gradientId}-reveal`}>
              <motion.rect
                key={dates[0]}
                x={-4}
                y={-8}
                height={LINE_H + 16}
                initial={{ width: 0 }}
                animate={{ width: innerW + 8 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              />
            </clipPath>
            <g clipPath={`url(#${gradientId}-reveal)`}>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <path className="hv-team-line" d={linePath} />
            </g>

            {values.map((v, i) => {
              if (!v?.point?.events.length || i === 0) return null
              return (
                <circle
                  key={v.date}
                  className="hv-event-dot"
                  cx={xs[i]}
                  cy={y(v.score)}
                  r={v.date === pinnedDate ? 6 : 4}
                  style={{ fill: team.accent }}
                />
              )
            })}

            <g transform={`translate(0,${barsTop})`}>
              {deltas.map((d, i) => {
                if (Math.abs(d.delta) < 1e-6) return null
                const top = d.delta > 0 ? yd(d.delta) : yd(0)
                const h = Math.max(1, Math.abs(yd(d.delta) - yd(0)))
                return (
                  <rect
                    key={d.date}
                    className={`hv-delta-bar ${d.delta > 0 ? 'is-up' : 'is-down'} ${d.date === pinnedDate ? 'is-pinned' : ''}`}
                    x={xs[i] - barW / 2}
                    y={top}
                    width={barW}
                    height={h}
                    rx={Math.min(2, barW / 2)}
                  />
                )
              })}
            </g>

            {hoverValue && <circle className="hv-hover-dot" cx={xs[hoverIdx]} cy={y(hoverValue.score)} r={5} />}

            <rect
              className="hv-hit"
              width={innerW}
              height={barsTop + BARS_H}
              onPointerMove={handleMove}
              onPointerLeave={() => setHoverIdx(null)}
              onClick={() => hoverDate && onPinDate(hoverDate)}
            />
          </g>
        </svg>
      )}

      {hoverValue && (
        <div
          className="hv-tooltip"
          style={{ left: Math.min(MARGIN.left + xs[hoverIdx] + 16, width - 270), top: 12 }}
        >
          <div className="hv-tooltip-date">{formatDay(hoverDate)}</div>
          <div className="hv-tooltip-stats">
            <span><b>{formatScore(hoverValue.score)}</b> {hoverChange && <ScoreDelta value={hoverChange.scoreDelta} />}</span>
            <span>#{hoverValue.rank} {hoverChange && <RankDelta value={hoverChange.rankDelta} size="sm" />}</span>
          </div>
          {hoverChange?.events.slice(0, 4).map((e, i) => (
            <div key={i} className="hv-tooltip-event">
              <span className={`hv-dot hv-dot--${e.kind}`} />
              {describeEvent(e, league).verb} {e.kind !== 'note' && e.name}
            </div>
          ))}
          {hoverChange?.events.length > 4 && <div className="hv-tooltip-more">+{hoverChange.events.length - 4} more</div>}
          <div className="hv-tooltip-hint">Click to see this day</div>
        </div>
      )}
    </div>
  )
}
